import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { useServerEvent } from '@/integration/runtime/server-events.js';
import {
  dismissSuggestion,
  reportSuggestionOpened,
  fetchSuggestionCandidates,
  type SkillSuggestion,
  type SkillSuggestionCandidate,
} from '@/lib/api/skill-suggestions.js';
import { relativeTime } from '@/ui/chat/relative-time.js';

interface SuggestedSkillsSectionProps {
  /** Opens the editor prefilled from this draft, exactly as the inline offer does. */
  onOpenEditor: (suggestion: SkillSuggestion) => void;
}

/**
 * Where the work happened, in one short line.
 *
 * The card used to carry a trigger, a title and a reason and nothing else, and that is how an offer
 * about a Jira export came to sit beside two Outlook chats reading like an invention — it had been
 * raised 40 minutes earlier, in a conversation the user had since left, and the card could not say so.
 * The conversation's own name plus the offer's age answers the question the user actually asks, which is
 * not "what is this" but "why is this here".
 */
function provenance(candidate: SkillSuggestionCandidate): string | null {
  const age =
    candidate.createdAt === null
      ? null
      : relativeTime(new Date(candidate.createdAt).getTime());
  if (candidate.conversationTitle !== null && age !== null) {
    return `from “${candidate.conversationTitle}” · ${age}`;
  }
  if (candidate.conversationTitle !== null) {
    return `from “${candidate.conversationTitle}”`;
  }
  return age;
}

/**
 * The pull half of smart skill suggestions: everything the platform noticed and the user has not
 * answered, waiting where skills are managed.
 *
 * This section is what lets the inline offer stay as restrained as it is. Inline, the platform speaks
 * once, quietly, and then gets out of the way — an offer nobody answers times out rather than sitting
 * there for ever. Without somewhere for those to land, that restraint would mean losing them; with it,
 * "we found something" stops implying "we interrupted you". The user meets these while already thinking
 * about skills, which is the one moment the question is genuinely welcome.
 *
 * The list is the user's own and is NOT scoped to the workspace they happen to be in. It was, on the
 * reasoning that a suggestion is workspace-bound because its task signature is — but the skill they
 * create from it is private either way, so the filter only hid work they had already done, and an offer
 * that disappears when you switch workspace reads as an offer that was withdrawn.
 *
 * Each offer is a card with fixed vertical slots, in the same order as a real skill's card one section
 * above: title, trigger, reason, where it came from, then the row of controls. It used to be a single wrapping
 * flex row, and the wrap order was decided by string length rather than by design — measured on the
 * stand at one width, a long-titled offer broke into four lines while its short-titled neighbour ran
 * title and reason together on one, so two rows of identical data had no shape in common and the reason
 * read as part of the title. Slots cannot reorder, so a long string now costs a taller card and nothing
 * else.
 *
 * Two up, sized like the skill cards above, and the columns are decided by a **container** query rather
 * than a viewport one. That distinction is the whole reason this reads right: the pane is a panel inside a
 * modal at roughly 0.6 of the window minus a fixed sidebar, so a viewport breakpoint knows nothing about
 * how much room these cards actually have — `sm:grid-cols-2` puts two 137px cards in a 318px pane, and one
 * 780px card in a pane wide enough for three. A single full-width card was the first attempt and it was
 * measured wrong at one width: fine in a 510px pane, four short lines stretched across 1250px with the
 * buttons opening a void underneath. Second columns appear when a column can hold this card's content,
 * which is what `@lg` marks.
 *
 * The slots are the grid's own rows — `grid-rows-subgrid`, four tracks per card — so two cards side by
 * side put their reason, their provenance and their buttons on the same lines. Reported: with each card
 * sizing its own rows, one title wrapping to two lines slid everything below it down and the two cards
 * read as different components. A track is as tall as the tallest card needs, so nothing is clamped to
 * buy the alignment; a shorter title simply leaves slack under itself. Title and trigger stay one track,
 * and deliberately: they are one unit, 2px apart, and giving each its own row would let the trigger drift
 * a line away from the name it belongs to. The `gap-y` on the card is dead weight where subgrid works —
 * the parent's gutters win in the subgridded axis — and the only thing standing between an old webview
 * and four slots with no space between them.
 *
 * The provenance gets a line to itself rather than sharing one with the buttons: it carries a conversation
 * title AND the offer's age, and the age is the half that answers "why is this here" — beside two buttons
 * it was the half that truncated away. It clamps to two lines instead of truncating, so a narrow column
 * costs a taller card rather than the age. It renders unconditionally, empty when the server sends no
 * provenance, because a card that skips the slot borrows the next one and lands its buttons on the
 * provenance line of the card beside it. The border is dashed because this card is the one thing in the
 * pane that is not a skill yet.
 *
 * Renders nothing at all when there is nothing waiting. An empty state here would be a permanent
 * advertisement for a feature that has, correctly, found nothing to say.
 */
export function SuggestedSkillsSection({
  onOpenEditor,
}: SuggestedSkillsSectionProps): JSX.Element | null {
  const [candidates, setCandidates] = useState<SkillSuggestionCandidate[]>([]);
  const [reloads, setReloads] = useState(0);
  /** Offers already reported as opened from this list, so a second Review says nothing new. */
  const opened = useRef(new Set<string>());

  useEffect(() => {
    let live = true;
    void fetchSuggestionCandidates().then((rows) => {
      if (live) setCandidates(rows.filter((row) => row.draft !== null));
    });
    return () => {
      live = false;
    };
  }, [reloads]);

  /**
   * Re-read when an offer is raised while this list is open.
   *
   * The judge answers 15-25 seconds after the turn finishes, which is long enough for a user who went
   * looking to be standing right here when it lands. Without this the list was fetched once on mount and
   * never again, so it would sit empty in front of an offer that already existed — the exact case that
   * reads as "the platform never suggested anything".
   */
  useServerEvent(
    'skill.suggestion',
    useCallback(() => setReloads((count) => count + 1), []),
  );

  if (candidates.length === 0) return null;

  const drop = (id: string): void => {
    setCandidates((rows) => rows.filter((row) => row.id !== id));
    void dismissSuggestion(id);
  };

  /**
   * Reviewing an offer is opening it — the same fact the inline line reports when it is expanded, and
   * reported once for the same reason: the first open is the one that carries meaning, and this section
   * stays mounted while the editor is open, so a second Review is not a second reading.
   */
  const review = (candidate: SkillSuggestionCandidate): void => {
    if (!opened.current.has(candidate.id)) {
      opened.current.add(candidate.id);
      void reportSuggestionOpened(candidate.id);
    }
    onOpenEditor(candidate);
  };

  return (
    <section
      aria-label="Suggested skills"
      className="@container flex flex-col gap-3 rounded-xl bg-muted/40 p-4"
    >
      <div className="flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        <span aria-hidden="true">✦</span>
        <span>Suggested</span>
        <span className="text-muted-foreground/70 normal-case">
          from work you have already done
        </span>
      </div>
      <ul className="grid grid-cols-1 gap-x-3 gap-y-2.5 @lg:grid-cols-2">
        {candidates.map((candidate) => (
          <li
            key={candidate.id}
            className="row-span-4 grid grid-rows-subgrid gap-y-2.5 rounded-xl border border-dashed border-border bg-card/40 p-4"
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-foreground">
                {candidate.draft?.title}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                /{candidate.draft?.trigger}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {candidate.reason}
            </p>
            <span className="line-clamp-2 text-[11px] text-muted-foreground/70">
              {provenance(candidate)}
            </span>
            <div className="flex items-end justify-end gap-2">
              <Button size="sm" onClick={() => review(candidate)}>
                Review
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => drop(candidate.id)}
              >
                Not needed
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
